import { supabase } from "@/integrations/supabase/client";

export type EntitlementAction =
  | "renew"
  | "revoke"
  | "enable"
  | "disable"
  | "demo_grant"
  | "grant_access"
  | "ensure_independent_context"
  | "assign_organisation";

/** Kinds of assessment context an account can belong to. */
export type OrgType =
  | "government"
  | "local_authority"
  | "ngo"
  | "university"
  | "healthcare"
  | "private"
  | "community"
  | "independent"
  | "other";

export const ORG_TYPE_LABELS: Record<OrgType, string> = {
  government: "Government",
  local_authority: "Local authority",
  ngo: "Organisation / NGO",
  university: "University / Research",
  healthcare: "Healthcare",
  private: "Private sector",
  community: "Community group",
  independent: "Independent",
  other: "Other",
};

/** Options shown when we ask "What are you representing?". */
export const REPRESENTATION_OPTIONS: { value: OrgType; label: string; desc: string }[] = [
  { value: "local_authority", label: "Government / Local authority", desc: "Council, city or public body" },
  { value: "ngo", label: "Organisation / NGO", desc: "Charity, association or non-profit" },
  { value: "university", label: "University / Research", desc: "Academic or research institution" },
  { value: "healthcare", label: "Healthcare provider", desc: "Hospital, care or health service" },
  { value: "private", label: "Private sector", desc: "Company or vendor" },
  { value: "community", label: "Community group", desc: "Local or resident-led group" },
  { value: "independent", label: "Myself / Independent", desc: "Acting as an individual" },
  { value: "other", label: "Other", desc: "Something else" },
];

export interface EntitlementRequest {
  /** Target organisation. Optional when `user_id` is supplied (except for grants). */
  organisation_id?: string;
  /** Target user — the server resolves their organisation. */
  user_id?: string;
  action: EntitlementAction;
  /** Renewal period in days. Ignored when `expires_at` is provided. */
  days?: number;
  /** Explicit ISO expiry, or `null` for unlimited (standard renewals only). */
  expires_at?: string | null;
  reason: string;
}

export interface EntitlementResult {
  ok: true;
  organisation_id: string;
  user_id?: string | null;
  action: EntitlementAction;
  source: "standard" | "demo";
  enabled: boolean;
  expires_at: string | null;
  previous_expires_at: string | null;
}

/** Derived per-user access status shown in Administration → Users & Roles. */
export type AccessStatus =
  | "active"
  | "expiring"
  | "expired"
  | "demo"
  | "revoked"
  | "not_granted";

export const ACCESS_STATUS_LABELS: Record<AccessStatus, string> = {
  active: "Active",
  expiring: "Expiring soon",
  expired: "Expired",
  demo: "Demo / Test",
  revoked: "Revoked",
  not_granted: "Not granted",
};

export const EXPIRING_SOON_DAYS = 30;

export interface OrgEntitlement {
  id: string;
  name: string;
  enabled: boolean;
  expiresAt: string | null;
  source: "standard" | "demo" | null;
  memberCount: number;
  orgType: OrgType;
  ownerUserId: string | null;
}

export const isIndependent = (org: OrgEntitlement | null | undefined) =>
  org?.orgType === "independent";

/** How a change to this context should be described to an administrator. */
export function scopeNote(org: OrgEntitlement | null | undefined): string {
  if (!org) return "This user has no assessment context yet.";
  if (isIndependent(org)) return "This is an independent context — the change affects this user only.";
  return `Access is organisation-wide — this affects all ${org.memberCount} member${
    org.memberCount === 1 ? "" : "s"
  } of ${org.name}.`;
}

/**
 * Entitlement lives on the assessment context (an organisation, or a person's
 * own independent context); this derives the status shown against each user.
 */
export function deriveAccessStatus(org: OrgEntitlement | null | undefined): AccessStatus {
  if (!org) return "not_granted";
  if (!org.enabled) return "revoked";
  if (!org.expiresAt) return org.source === "demo" ? "demo" : "active";
  const ms = new Date(org.expiresAt).getTime() - Date.now();
  if (ms <= 0) return "expired";
  if (org.source === "demo") return "demo";
  if (ms <= EXPIRING_SOON_DAYS * 86_400_000) return "expiring";
  return "active";
}

export async function fetchOrgEntitlements(): Promise<Map<string, OrgEntitlement>> {
  const [{ data: orgs, error }, { data: profs }] = await Promise.all([
    supabase
      .from("organisations")
      .select(
        "id, name, org_type, owner_user_id, module_config, assessment_access_expires_at, assessment_access_source",
      ),
    supabase.from("profiles").select("id, organisation_id"),
  ]);
  if (error) throw new Error(error.message);

  const counts = new Map<string, number>();
  for (const p of profs || []) {
    if (p.organisation_id) {
      counts.set(p.organisation_id, (counts.get(p.organisation_id) || 0) + 1);
    }
  }

  const map = new Map<string, OrgEntitlement>();
  for (const o of orgs || []) {
    const config = (o.module_config ?? {}) as Record<string, unknown>;
    map.set(o.id, {
      id: o.id,
      name: o.name,
      enabled: config.assessment !== false,
      expiresAt: o.assessment_access_expires_at,
      source: (o.assessment_access_source as "standard" | "demo" | null) ?? "standard",
      memberCount: counts.get(o.id) || 0,
      orgType: ((o as { org_type?: string }).org_type as OrgType) ?? "other",
      ownerUserId: (o as { owner_user_id?: string | null }).owner_user_id ?? null,
    });
  }
  return map;
}



export interface EntitlementEvent {
  id: string;
  organisation_id: string;
  module: string;
  action: string;
  source: string;
  previous_expires_at: string | null;
  new_expires_at: string | null;
  previous_enabled: boolean | null;
  new_enabled: boolean | null;
  reason: string | null;
  actor_email: string | null;
  created_at: string;
}

export const RENEWAL_PRESETS = [
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "180 days", days: 180 },
  { label: "365 days", days: 365 },
] as const;

export const DEMO_PRESETS = [
  { label: "7 days", days: 7 },
  { label: "14 days", days: 14 },
  { label: "30 days", days: 30 },
] as const;

/**
 * All entitlement changes go through the server. The client can never write
 * expiry dates directly — the edge function re-validates the admin role and
 * writes the audit record atomically.
 */
export async function updateEntitlement(
  req: EntitlementRequest,
): Promise<EntitlementResult> {
  const { data, error } = await supabase.functions.invoke("renew-entitlement", {
    body: req,
  });
  if (error) {
    // Surface the server's message when available.
    let message = error.message;
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === "function") {
      try {
        const body = await ctx.json();
        if (body?.error) message = body.error;
      } catch {
        /* keep default */
      }
    }
    throw new Error(message);
  }
  if (data?.error) throw new Error(data.error);
  return data as EntitlementResult;
}

export async function fetchEntitlementEvents(
  organisationId: string,
  limit = 10,
): Promise<EntitlementEvent[]> {
  const { data, error } = await supabase
    .from("entitlement_events")
    .select(
      "id, organisation_id, module, action, source, previous_expires_at, new_expires_at, previous_enabled, new_enabled, reason, actor_email, created_at",
    )
    .eq("organisation_id", organisationId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data || []) as EntitlementEvent[];
}

export const formatDate = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "No expiry";
